import { useEffect, useRef } from "react";

export interface MediaArtwork {
  src: string;
  sizes?: string;
  type?: string;
}

export interface MediaSessionMetadataOptions {
  title?: string;
  artist?: string;
  album?: string;
  artwork?: MediaArtwork[];
}

export interface UseMediaSessionProps {
  metadata?: MediaSessionMetadataOptions;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onSeekBackward?: (offsetSeconds?: number) => void;
  onSeekForward?: (offsetSeconds?: number) => void;
  onSeekTo?: (timeSeconds: number) => void;
  onStop?: () => void;
}

/**
 * Hook to synchronize web and mobile PWA playback state with the OS Media Session API.
 * Enables lock-screen controls, notification bar media controls, and hardware media keys.
 */
export function useMediaSession({
  metadata,
  isPlaying,
  currentTime,
  duration,
  playbackRate = 1,
  onPlay,
  onPause,
  onSeekBackward,
  onSeekForward,
  onSeekTo,
  onStop,
}: UseMediaSessionProps): void {
  // Use refs for callbacks to avoid re-registering action handlers on every tick
  const callbacksRef = useRef({
    onPlay,
    onPause,
    onSeekBackward,
    onSeekForward,
    onSeekTo,
    onStop,
  });

  useEffect(() => {
    callbacksRef.current = {
      onPlay,
      onPause,
      onSeekBackward,
      onSeekForward,
      onSeekTo,
      onStop,
    };
  });

  // Register & update MediaMetadata
  useEffect(() => {
    if (typeof window === "undefined" || !("mediaSession" in navigator)) {
      return;
    }

    if (metadata && metadata.title) {
      try {
        const artworkList: MediaArtwork[] =
          metadata.artwork && metadata.artwork.length > 0
            ? metadata.artwork
            : [
                {
                  src: "/favicon.svg",
                  sizes: "512x512",
                  type: "image/svg+xml",
                },
              ];

        // Global MediaMetadata constructor check
        if (typeof MediaMetadata !== "undefined") {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: metadata.title,
            artist: metadata.artist || "HP - Helix",
            album: metadata.album || "HP - Helix Streaming",
            artwork: artworkList,
          });
        }
      } catch (err) {
        console.warn("[useMediaSession] Could not set MediaMetadata:", err);
      }
    }
  }, [metadata?.title, metadata?.artist, metadata?.album, metadata?.artwork]);

  // Register action handlers once (or when support is verified)
  useEffect(() => {
    if (typeof window === "undefined" || !("mediaSession" in navigator)) {
      return;
    }

    const session = navigator.mediaSession;

    const actionHandlers: Array<[MediaSessionAction, MediaSessionActionHandler]> = [
      [
        "play",
        () => {
          callbacksRef.current.onPlay?.();
        },
      ],
      [
        "pause",
        () => {
          callbacksRef.current.onPause?.();
        },
      ],
      [
        "seekbackward",
        (details) => {
          const offset = details.seekOffset || 10;
          callbacksRef.current.onSeekBackward?.(offset);
        },
      ],
      [
        "seekforward",
        (details) => {
          const offset = details.seekOffset || 10;
          callbacksRef.current.onSeekForward?.(offset);
        },
      ],
      [
        "seekto",
        (details) => {
          if (typeof details.seekTime === "number") {
            callbacksRef.current.onSeekTo?.(details.seekTime);
          }
        },
      ],
      [
        "stop",
        () => {
          callbacksRef.current.onStop?.();
        },
      ],
    ];

    actionHandlers.forEach(([action, handler]) => {
      try {
        session.setActionHandler(action, handler);
      } catch {
        // Some browsers may not support all actions
      }
    });

    return () => {
      actionHandlers.forEach(([action]) => {
        try {
          session.setActionHandler(action, null);
        } catch {
          // Cleanup ignore
        }
      });
    };
  }, []);

  // Update playbackState ("playing" | "paused" | "none")
  useEffect(() => {
    if (typeof window === "undefined" || !("mediaSession" in navigator)) {
      return;
    }
    try {
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch {
      // Ignored
    }
  }, [isPlaying]);

  // Update positionState (duration, playbackRate, position)
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("mediaSession" in navigator) ||
      !("setPositionState" in navigator.mediaSession)
    ) {
      return;
    }

    if (
      typeof duration === "number" &&
      duration > 0 &&
      isFinite(duration) &&
      !isNaN(duration) &&
      typeof currentTime === "number" &&
      isFinite(currentTime) &&
      !isNaN(currentTime)
    ) {
      try {
        const safePosition = Math.min(Math.max(0, currentTime), duration);
        navigator.mediaSession.setPositionState({
          duration,
          playbackRate: playbackRate > 0 ? playbackRate : 1,
          position: safePosition,
        });
      } catch {
        // Ignored if out of bounds or unsupported
      }
    }
  }, [currentTime, duration, playbackRate]);
}
